// The competency matrix: who has been SHOWN ABLE, on what, and until when.
//
// THIS IS NOT THE SAME QUESTION AS AUTHORITY. A contractor covering maternity
// leave can hold CAPTURE on day one — a signature grants it. Whether they have
// been assessed able is a different claim, and it is the one an auditor tests
// during the cover. Both are checked at ingest and both fail closed.
//
// TWO THINGS THIS PAGE REFUSES TO HIDE:
//
//   - AN EXPIRED ASSESSMENT IS SHOWN AS EXPIRED, NEVER AS ABSENT. At the gate the
//     two behave identically: the reading is refused either way. To a person they
//     are opposite problems — "never assessed" sends somebody to be trained,
//     "lapsed on the 3rd" sends them to be re-assessed, and those are different
//     conversations with different people. A matrix that drops expired rows shows
//     an empty cell and loses which one it is.
//   - LEVEL 0 IS NOT COMPETENT. It is a real assessment, with a real assessor,
//     recording that the person is not yet able. It must never read as a tick.
//
// Scope matters too: being competent on the moisture analyser is not being
// competent on the chloride titrator, so each chip names the parameter and
// instrument it covers, and a blank scope means "everything for this activity".
import { useMemo, useState, type FormEvent } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { api } from '../../lib/api';
import { activityLabel, activityOption } from '../../lib/vocabulary';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import {
  grantHint,
  localDate,
  localInstant,
  officeFocus,
  rows,
  serverMessage,
  useMyGrants,
  useResource,
} from '../../shells/OfficeShell';

type Activity = 'CAPTURE' | 'RECEIPT_INSPECT' | 'DISPOSITION' | 'SPEC_APPROVE' | 'VERIFY';

// VERIFY is assessed like any other activity: both schemes expect a verifier to
// be trained, so a shift leader with the grant and no assessment is refused at
// the point of signing, with a message saying which of the two is missing.
const ACTIVITIES: Activity[] = [
  'CAPTURE',
  'RECEIPT_INSPECT',
  'DISPOSITION',
  'SPEC_APPROVE',
  'VERIFY',
];
const LEVELS = [0, 25, 50, 75, 100];

interface Competency {
  id: number;
  upn: string;
  activity: Activity;
  parameter: string | null;
  instrument: string | null;
  level: number;
  expiresAt: string;
  assessedByUpn: string;
  assessedAt: string;
  sourceRef: string | null;
}

function day(iso: string | null): string {
  if (!iso) return '—';
  return localDate(iso) ?? iso;
}

type CellState = 'able' | 'expiring' | 'expired' | 'not-able';

function stateOf(c: Competency, now: number): CellState {
  const expires = Date.parse(c.expiresAt);
  // An expiry this screen cannot read is treated as expired, not as able. The
  // gate fails closed on ExpiresAt, so a row rendered green off an unparseable
  // date would be telling the office that somebody can capture while the server
  // refuses every reading they take.
  if (!Number.isFinite(expires) || expires <= now) return 'expired';
  if (c.level <= 0) return 'not-able';
  if (expires - now <= 30 * 86_400_000) return 'expiring';
  return 'able';
}

const CHIP: Record<CellState, string> = {
  able: 'border-(--ok-line) bg-(--ok-bg) text-(--ok)',
  expiring: 'border-(--warn-line) bg-(--warn-bg) text-(--warn)',
  expired: 'border-(--bad-line) bg-(--bad-bg) text-(--bad)',
  'not-able': 'border-(--warn-line) bg-(--warn-bg) text-(--warn)',
};

/**
 * A chip's words, kept short and never wrapped (UI/UX audit, 31 Oct 2026, UX-32): on an iPad the
 * matrix's five activity columns squeezed "level 75 · to 2027-01-29" beside its scope into a
 * four-line pill. The level and the date are what a reader scans for; `chipTitle` carries the
 * sentence. "not assessed" is untouched and stays visible.
 */
function chipText(c: Competency, state: CellState): string {
  if (state === 'expired') return `expired ${day(c.expiresAt)}`;
  if (state === 'not-able') return 'not yet able';
  return `L${c.level} to ${day(c.expiresAt)}`;
}

function chipTitle(c: Competency, state: CellState): string {
  const scope = scopeText(c) === 'all' ? 'every parameter and instrument' : scopeText(c);
  if (state === 'expired') return `Expired ${day(c.expiresAt)} (${scope}): refused like a missing assessment.`;
  if (state === 'not-able') return `Assessed at level 0, not yet able (${scope}): refused at the gate.`;
  return `Level ${c.level}, expires ${day(c.expiresAt)} (${scope}).`;
}

function scopeText(c: Competency): string {
  const bits = [c.parameter, c.instrument].filter(Boolean);
  return bits.length > 0 ? bits.join(' · ') : 'all';
}

/** One in-force grant, as GET /authorisations?activeOnly=true returns it. */
interface Grant {
  upn: string;
  action: string;
}

/**
 * The activity each grant is refused without (UI/UX audit, 31 Oct 2026, UX-07): CAPTURE is
 * checked against a CAPTURE assessment at ingest, VERIFY against VERIFY at the point of signing,
 * and RELEASE against RECEIPT_INSPECT when a certificate is decided (goodsInDecision.ts). A subset
 * of AuthAction, not a rule 14 mirror: only the grants a competency gate stands behind.
 */
export const GRANT_ACTIVITY = { CAPTURE: 'CAPTURE', VERIFY: 'VERIFY', RELEASE: 'RECEIPT_INSPECT' } as const;
export type GatedGrant = keyof typeof GRANT_ACTIVITY;

/**
 * Whether an assessment would let this grant act now. The gate's own reading (authority.ts
 * checkCompetency): not expired and above level 0. The certificate decision asks with no
 * parameter or instrument, so only an unscoped RECEIPT_INSPECT row answers it; CAPTURE and VERIFY
 * are asked per parameter, so any current row counts here and its chip names the scope it covers.
 */
export function assessedFor(rows: Competency[], upn: string, grant: GatedGrant, now: number): boolean {
  const activity = GRANT_ACTIVITY[grant];
  return rows.some((c) => {
    if (c.upn !== upn || c.activity !== activity) return false;
    if (grant === 'RELEASE' && (c.parameter || c.instrument)) return false;
    const state = stateOf(c, now);
    return state === 'able' || state === 'expiring';
  });
}

/** One row of the matrix: an account, and its assessments per activity, latest
 *  expiry first, each with the state it is in now. `unassessed` names the in-force grants on
 *  this account that no current assessment stands behind (UX-07). */
export interface MatrixRow {
  upn: string;
  cells: Record<Activity, Array<{ c: Competency; state: CellState }>>;
  unassessed: GatedGrant[];
}

/**
 * Every account that has an assessment OR holds a gated grant (UX-07). Built from /competencies
 * alone, the matrix left out exactly the people it exists to catch: ajay@, gurlal@, jamie@ and
 * production@ held CAPTURE / VERIFY with no assessment and had no row, so the page read as if
 * everyone with authority had been assessed. A grant without a current assessment is refused
 * at the gate outside a pilot run (rule 27 skips competency only on a pilot run, and never on a
 * certificate decision), so it is a row with a warning, not an absence.
 */
export function buildMatrix(all: Competency[], inForce: Grant[], now: number): MatrixRow[] {
  const gated = inForce.filter((g): g is Grant & { action: GatedGrant } => g.action in GRANT_ACTIVITY);
  const people = [...new Set([...all.map((c) => c.upn), ...gated.map((g) => g.upn)])].sort((a, b) =>
    a.localeCompare(b),
  );
  return people.map((upn) => ({
    upn,
    cells: Object.fromEntries(
      ACTIVITIES.map((a) => [
        a,
        all
          .filter((c) => c.upn === upn && c.activity === a)
          .sort((x, y) => Date.parse(y.expiresAt) - Date.parse(x.expiresAt))
          .map((c) => ({ c, state: stateOf(c, now) })),
      ]),
    ) as MatrixRow['cells'],
    unassessed: [...new Set(gated.filter((g) => g.upn === upn).map((g) => g.action))]
      .filter((a) => !assessedFor(all, upn, a, now))
      .sort(),
  }));
}

/** The warn chip's words. RELEASE's gate has no pilot exemption, so it does not claim one. */
/** The chip's short words (UX-32); the full sentence above is its title and its screen-reader text. */
export function unassessedShort(g: GatedGrant): string {
  return g === 'RELEASE' ? 'RELEASE: no RECEIPT_INSPECT' : `${g}: not assessed`;
}

export function unassessedText(g: GatedGrant): string {
  return g === 'RELEASE'
    ? 'RELEASE granted, not assessed (RECEIPT_INSPECT): certificate decisions refused'
    : `${g} granted, not assessed: refused outside a pilot run`;
}

/**
 * The instruments an assessment can be scoped to (UI/UX audit, 31 Oct 2026, UX-51): the kinds of
 * the registered meters, distinct and sorted. The gate asks with the reading's METER KIND
 * (ingestGate: `instrument: meter?.kind ?? null`) and matches it exactly, so a scope typed freely
 * ("Moisture analyser" for kind "MOISTURE") was an assessment that silently covered nothing. Only
 * a kind a meter has can ever be asked for, so only those are offered, beside "All".
 */
export function instrumentKinds(meters: Array<{ kind: string }>): string[] {
  return [...new Set(meters.map((m) => m.kind.trim()).filter((k) => k !== ''))].sort((a, b) => a.localeCompare(b));
}

/** Parameter codes already used in an assessment: suggestions for the code box, never a limit. */
export function knownParameters(all: Array<{ parameter: string | null }>): string[] {
  return [...new Set(all.map((c) => c.parameter?.trim() ?? '').filter((p) => p !== ''))].sort((a, b) => a.localeCompare(b));
}

/** Worst first when an activity column is sorted; never assessed sorts last. */
const STATE_RANK: CellState[] = ['expired', 'not-able', 'expiring', 'able'];
const worst = (cells: MatrixRow['cells'][Activity]) =>
  cells.length === 0 ? STATE_RANK.length : Math.min(...cells.map((x) => STATE_RANK.indexOf(x.state)));

const col = createColumnHelper<MatrixRow>();
const columns = [
  // Pinned (stickyFirst) and allowed to break inside the address (UI/UX audit, 31 Oct 2026, UX-15):
  // at iPad width the VERIFY column was clipped off the right edge, away from whose row it was.
  col.accessor('upn', {
    header: 'Account',
    cell: (ctx) => <span className="[overflow-wrap:anywhere]">{ctx.getValue()}</span>,
    meta: { rowHeader: true, width: '12rem' },
  }),
  // Its own column rather than a chip in an activity's cell: RELEASE's gap lives under
  // RECEIPT_INSPECT, and a warning about a grant reads best beside the account (UX-07).
  col.accessor((r) => r.unassessed.map(unassessedText).join(' '), {
    id: 'grants',
    header: 'Grants',
    sortingFn: (x, y) => y.original.unassessed.length - x.original.unassessed.length,
    cell: (ctx) => {
      const gaps = ctx.row.original.unassessed;
      return gaps.length === 0 ? (
        <span className="text-(--muted-2)">—</span>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-(--s1) p-0">
          {gaps.map((g) => (
            <li key={g}>
              <span
                title={unassessedText(g)}
                className={`inline-flex items-center whitespace-nowrap rounded-(--radius-pill) border px-(--s2) py-[2px] text-(length:--fs-xs) font-semibold ${CHIP.expiring}`}
              >
                <span aria-hidden="true">{unassessedShort(g)}</span>
                <span className="sr-only">{unassessedText(g)}</span>
              </span>
            </li>
          ))}
        </ul>
      );
    },
  }),
  ...ACTIVITIES.map((a) =>
    col.accessor(
      (r) => r.cells[a].map(({ c, state }) => `${scopeText(c)} ${chipText(c, state)}`).join(' ') || 'not assessed',
      {
        id: a,
        // Words, not the code (UI/UX audit, 31 Oct 2026, UX-17); the picker below still shows it.
        header: activityLabel(a),
        sortingFn: (x, y) => worst(x.original.cells[a]) - worst(y.original.cells[a]),
        cell: (ctx) => {
          const cells = ctx.row.original.cells[a];
          return cells.length === 0 ? (
            <span className="text-(--muted-2)">not assessed</span>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-(--s1) p-0">
              {cells.map(({ c, state }) => (
                <li key={c.id}>
                  <span
                    title={chipTitle(c, state)}
                    // Wraps (UX-15): one line per chip made each activity column as wide as its
                    // longest scope and state, and pushed VERIFY past the edge.
                    className={`inline-flex flex-wrap items-center gap-x-(--s2) rounded-(--radius-pill) border px-(--s2) py-[2px] text-(length:--fs-xs) font-semibold ${CHIP[state]}`}
                  >
                    <span className="font-mono">{scopeText(c)}</span>
                    <span>{chipText(c, state)}</span>
                  </span>
                </li>
              ))}
            </ul>
          );
        },
      },
    ),
  ),
];

const FIELD =
  'flex min-h-[44px] rounded-md border border-hairline-strong bg-card px-3 text-sm ' +
  'transition-[border-color,box-shadow] duration-150 motion-reduce:transition-none';
const LABEL =
  'text-[13px] font-semibold text-muted-foreground';

export function Competency() {
  const register = useResource<Competency[]>('/competencies');
  // In force only: a lapsed grant is not authority, and the server applies the same window (UX-07).
  const granted = useResource<Grant[]>('/authorisations?activeOnly=true');
  // The instrument kinds an assessment may name (UX-51). Rule 15: unread is not "no instruments".
  const meters = useResource<{ meters?: Array<{ kind: string }> }>('/meters');
  const grants = useMyGrants();
  const now = Date.now();

  const [upn, setUpn] = useState('');
  const [activity, setActivity] = useState<Activity>('CAPTURE');
  // "All" is a choice, not a blank box (UX-51): the runbook's two pilot records are both "All",
  // and naming a parameter narrows the record to that one code, matched exactly.
  const [parameterScope, setParameterScope] = useState<'all' | 'one'>('all');
  const [parameter, setParameter] = useState('');
  const [instrument, setInstrument] = useState('');
  const [level, setLevel] = useState('75');
  const [expiresAt, setExpiresAt] = useState('');
  const [assessedAt, setAssessedAt] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // What the last Record recorded (audit finding 15): shown until the next submit.
  const [saved, setSaved] = useState<string | null>(null);

  const hint = grantHint(grants, 'ASSESS', 'Recording an assessment');
  const canAssess = hint === null;

  const all = rows<Competency>(register.data);

  // BOTH reads must have answered before the matrix claims anything (rule 15; useMyGrants'
  // `known` shape). With only /competencies read, the unassessed grant holders would vanish again
  // and the page would look complete, so either failure is "Not known" for the whole matrix.
  const known =
    register.data !== null && register.error === null && granted.data !== null && granted.error === null;
  const readError = register.error ?? granted.error;
  const retry = () => {
    register.reload();
    granted.reload();
  };

  const matrix = useMemo<MatrixRow[]>(
    () => (known ? buildMatrix(all, rows<Grant>(granted.data), Date.now()) : []),
    [known, all, granted.data],
  );
  const unassessedCount = matrix.reduce((n, r) => n + r.unassessed.length, 0);

  async function assess(e: FormEvent) {
    e.preventDefault();
    if (!canAssess) return;

    // LOCAL instants, not the raw date strings. The API refuses an assessedAt in
    // the future, and a bare "2026-09-16" is UTC midnight — which, at UTC+10, is
    // still hours away for the whole first half of the working day. Posted raw,
    // recording an assessment made this morning is refused until 10am.
    const assessedInstant = localInstant(assessedAt);
    const expiresInstant = localInstant(expiresAt);
    if (!assessedInstant || !expiresInstant) {
      setRefusal('Both dates are required: when the assessment was made, and when it expires.');
      return;
    }

    setSaving(true);
    setRefusal(null);
    setSaved(null);
    try {
      // AssessedByUpn is not sent: the assessor is the signed-in person on the
      // validated token. The API also requires that person to hold ASSESS at the
      // moment of assessment, which a form field could only ever claim.
      const account = upn.trim().toLowerCase();
      const param = parameterScope === 'one' ? parameter.trim() : '';
      const scope = [param, instrument.trim()].filter(Boolean);
      await api.post('/competencies', {
        upn: account,
        activity,
        parameter: param || null,
        instrument: instrument.trim() || null,
        level: Number(level),
        expiresAt: expiresInstant,
        assessedAt: assessedInstant,
        sourceRef: sourceRef.trim() || null,
      });
      setSaved(
        `Assessment recorded: ${activity}${scope.length > 0 ? ` (${scope.join(' · ')})` : ''} for ${account}, ${
          Number(level) === 0 ? 'assessed not yet able' : `level ${level}`
        }, expires ${day(expiresInstant)}.`,
      );
      setUpn('');
      setParameterScope('all');
      setParameter('');
      setInstrument('');
      setExpiresAt('');
      setAssessedAt('');
      setSourceRef('');
      register.reload();
    } catch (err: unknown) {
      setRefusal(serverMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const kinds = meters.data !== null && meters.error === null ? instrumentKinds(rows(meters.data?.meters)) : null;
  const paramSuggestions = knownParameters(all);

  const expiredCount = all.filter((c) => stateOf(c, now) === 'expired').length;

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Competency</h1>
        <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
          Who has been assessed able. An expired assessment refuses captures, like a missing one.
        </p>
      </header>


      {known && unassessedCount > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
          {unassessedCount} {unassessedCount === 1 ? 'grant has' : 'grants have'} no current assessment behind
          it. Outside a pilot run each one is refused at the gate; a certificate decision is refused in a pilot too.
        </p>
      ) : null}

      {expiredCount > 0 ? (
        <p className="m-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3) text-(length:--fs-sm) font-semibold text-(--warn)">
          {expiredCount} {expiredCount === 1 ? 'assessment has' : 'assessments have'} expired. Every
          one of them is currently refusing a capture.
        </p>
      ) : null}

      {/* A failed read of EITHER list is "Not known" for the whole matrix, said in this page's
          words with a way to ask again (rule 15), never a table of whoever the other list held. */}
      {readError ? (
        <div className="flex flex-wrap items-center gap-(--s3)">
          <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
            Not known: {register.error ? 'the assessments' : 'the grants in force'} could not be read, so who is
            assessed for what is not shown. {readError}
          </p>
          <Button variant="outline" size="sm" onClick={retry}>Try again</Button>
        </div>
      ) : (
        <DataTable
          data={matrix}
          columns={columns}
          caption="Competency by account and activity"
          getRowId={(r) => r.upn}
          stickyFirst
          search="Search account, scope or state"
          loading={!known}
          empty="Nobody has been assessed and nobody holds CAPTURE, VERIFY or RELEASE."
        />
      )}

      <section className="rounded-(--radius) border border-(--border) bg-(--card) shadow-card p-(--s4)">
        <h2 className="m-0 font-display text-[22px] font-semibold leading-tight">Record an assessment</h2>
        <form onSubmit={assess} className="mt-(--s3) flex flex-wrap items-end gap-(--s3)">
          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-upn" className={LABEL}>Account</label>
            <input
              id="c-upn"
              type="email"
              required
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="name@majans.com"
              className={`${FIELD} min-w-[240px] ${officeFocus}`}
            />
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-activity" className={LABEL}>Activity</label>
            <select
              id="c-activity"
              value={activity}
              onChange={(e) => setActivity(e.target.value as Activity)}
              className={`${FIELD} ${officeFocus}`}
            >
              {/* Each activity names the grant whose gate checks it (UI/UX audit, 31 Oct 2026, UX-17). */}
              {ACTIVITIES.map((a) => (
                <option key={a} value={a}>{activityOption(a)}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-parameter-scope" className={LABEL}>Parameter</label>
            <select
              id="c-parameter-scope"
              value={parameterScope}
              onChange={(e) => setParameterScope(e.target.value as 'all' | 'one')}
              className={`${FIELD} ${officeFocus}`}
            >
              <option value="all">All parameters</option>
              <option value="one">One parameter…</option>
            </select>
          </div>

          {parameterScope === 'one' ? (
            <div className="flex flex-col gap-(--s1)">
              <label htmlFor="c-parameter" className={LABEL}>Parameter code (exact)</label>
              {/* Suggestions from codes already assessed; the code must match the spec's
                  parameter code exactly, or the record covers no check at all. */}
              <input
                id="c-parameter"
                type="text"
                required
                list="c-parameter-codes"
                value={parameter}
                onChange={(e) => setParameter(e.target.value)}
                placeholder="Spec parameter code"
                className={`${FIELD} ${officeFocus}`}
              />
              <datalist id="c-parameter-codes">
                {paramSuggestions.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
          ) : null}

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-instrument" className={LABEL}>Instrument</label>
            {/* Registered meter kinds only (instrumentKinds). An unread meter list offers "All"
                alone and says so, rather than an empty list that reads as "no instruments". */}
            <select
              id="c-instrument"
              value={instrument}
              onChange={(e) => setInstrument(e.target.value)}
              aria-describedby={kinds === null && meters.error ? 'c-instrument-note' : undefined}
              className={`${FIELD} ${officeFocus}`}
            >
              <option value="">All instruments</option>
              {(kinds ?? []).map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            {kinds === null && meters.error ? (
              <span id="c-instrument-note" className="text-(length:--fs-xs) text-(--warn)">
                The instruments could not be read, so only All is offered.{' '}
                <button type="button" onClick={meters.reload} className={`underline ${officeFocus}`}>Try again</button>
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-level" className={LABEL}>Level</label>
            <select
              id="c-level"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className={`${FIELD} ${officeFocus}`}
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {/* Level 0 is recorded, and it is not a pass. Saying so in the
                      option text stops it being chosen by accident. */}
                  {l === 0 ? '0 — assessed not yet able' : l}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-assessed" className={LABEL}>Assessed on</label>
            <input
              id="c-assessed"
              type="date"
              required
              value={assessedAt}
              onChange={(e) => setAssessedAt(e.target.value)}
              className={`${FIELD} ${officeFocus}`}
            />
          </div>

          <div className="flex flex-col gap-(--s1)">
            <label htmlFor="c-expires" className={LABEL}>Expires</label>
            {/* Required by the schema, and the reason the gate can fail closed:
                an assessment with no end never lapses and never gets re-checked. */}
            <input
              id="c-expires"
              type="date"
              required
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className={`${FIELD} ${officeFocus}`}
            />
          </div>

          <div className="flex min-w-[240px] flex-1 flex-col gap-(--s1)">
            <label htmlFor="c-source" className={LABEL}>Evidence reference</label>
            <input
              id="c-source"
              type="text"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              placeholder="Where the assessment record lives"
              className={`${FIELD} w-full ${officeFocus}`}
            />
          </div>

          <span title={hint ?? 'Record this assessment'}>
            <Button variant="default"
              type="submit"
              disabled={!canAssess || saving}
              aria-describedby="assess-hint"
            >
              {saving ? 'Recording…' : 'Record'}
            </Button>
          </span>
        </form>

        <p id="assess-hint" aria-live="polite" className="m-0 mt-(--s2) text-(length:--fs-xs) text-(--muted)">
          {hint ?? 'Recorded against your account as the assessor.'}
        </p>
        {refusal ? (
          <p role="alert" className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">
            {refusal}
          </p>
        ) : null}
        {saved ? (
          <div className="mt-(--s2)">
            <Saved>{saved}</Saved>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default Competency;
